import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Rocket, Mail, Lock, KeyRound, ArrowLeft } from 'lucide-react';
import api from '../../lib/api';

const LOGO_URL = "/assets/sios-logo.png";

export const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('credentials'); // 'credentials' or 'otp'
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    otp_code: '',
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', {
        email: formData.email,
        password: formData.password,
        otp_code: step === 'otp' ? formData.otp_code : undefined
      });

      if (response.data.requires_2fa) {
        // Need to enter OTP
        setStep('otp');
        toast.success('Codice di verifica inviato alla tua email');
      } else {
        // Login successful
        await login(formData.email, formData.password, formData.otp_code);
        toast.success('Accesso effettuato');
        navigate('/admin');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante l\'accesso');
      if (step === 'otp') {
        setFormData({ ...formData, otp_code: '' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/verify-otp', {
        email: formData.email,
        otp_code: formData.otp_code
      });

      // Store token and user with correct keys
      const { access_token, user } = response.data;
      localStorage.setItem('sios_token', access_token);
      localStorage.setItem('sios_user', JSON.stringify(user));
      
      toast.success('Accesso effettuato');
      
      // Use full page reload to ensure AuthContext reinitializes with new token
      // Small delay to ensure localStorage write completes
      setTimeout(() => {
        window.location.replace('/admin');
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Codice non valido');
      setFormData({ ...formData, otp_code: '' });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      await api.post(`/auth/resend-otp?email=${encodeURIComponent(formData.email)}`);
      toast.success('Nuovo codice inviato');
    } catch (error) {
      toast.error('Errore nell\'invio del codice');
    }
  };

  const goBack = () => {
    setStep('credentials');
    setFormData({ ...formData, otp_code: '' });
  };

  return (
    <div className="min-h-screen bg-sios-void flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="SIOS" className="h-16 mx-auto mb-6" />
          </Link>
          <h1 className="font-heading text-2xl font-bold mb-2">
            {step === 'credentials' ? 'Accedi all\'Admin' : 'Verifica Identità'}
          </h1>
          <p className="text-muted-foreground text-sm">
            {step === 'credentials' 
              ? 'Accedi per gestire il contenuto SIOS' 
              : `Inserisci il codice inviato a ${formData.email}`
            }
          </p>
        </div>

        {step === 'credentials' ? (
          <form onSubmit={handleSubmit} className="glass rounded-2xl p-8 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10 bg-white/5 border-white/10"
                  required
                  data-testid="input-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 bg-white/5 border-white/10"
                  required
                  data-testid="input-password"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full rounded-full bg-primary hover:bg-primary/90 neon-shadow h-12"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <Rocket className="animate-bounce" size={18} />
              ) : (
                'Accedi'
              )}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP} className="glass rounded-2xl p-8 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="otp">Codice di Verifica</Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input
                  id="otp"
                  type="text"
                  placeholder="123456"
                  value={formData.otp_code}
                  onChange={(e) => setFormData({ ...formData, otp_code: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  className="pl-10 bg-white/5 border-white/10 text-center text-2xl tracking-widest"
                  maxLength={6}
                  required
                  autoFocus
                  data-testid="input-otp"
                />
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Il codice scade tra 10 minuti
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full rounded-full bg-primary hover:bg-primary/90 neon-shadow h-12"
              disabled={loading || formData.otp_code.length !== 6}
              data-testid="verify-btn"
            >
              {loading ? (
                <Rocket className="animate-bounce" size={18} />
              ) : (
                'Verifica'
              )}
            </Button>

            <div className="flex justify-between items-center text-sm">
              <button
                type="button"
                onClick={goBack}
                className="text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                <ArrowLeft size={16} />
                Indietro
              </button>
              <button
                type="button"
                onClick={handleResendOTP}
                className="text-primary hover:underline"
              >
                Rinvia codice
              </button>
            </div>
          </form>
        )}

        <p className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/" className="text-primary hover:underline">
            Torna al sito
          </Link>
        </p>
      </div>
    </div>
  );
};
